import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { HeartPulse, Filter, Save, X, Trash2, Check, Plus } from "lucide-react";
import DateField from "@/components/app/DateField";

const TYPE_LABEL = {
  muscular: "Muscular", articular: "Articular", osea: "Ósea", contusion: "Contusión", otro: "Otro",
};
const SEV_LABEL = {
  leve: "Leve", moderada: "Moderada", grave: "Grave",
};
const SEV_COLOR = {
  leve: "text-amber-400 border-amber-400/60",
  moderada: "text-orange-400 border-orange-400/60",
  grave: "text-destructive border-destructive/60",
};

function daysBetween(from, to) {
  if (!from) return null;
  const end = to ? new Date(to) : new Date();
  const start = new Date(from);
  return Math.max(0, Math.floor((end - start) / 86400000));
}

export default function InjuriesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [filterTeam, setFilterTeam] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const canWrite = ["super_admin", "club_admin", "coordinator", "director_deportivo", "coach", "delegado"].includes(user?.role);
  const canDelete = ["super_admin", "club_admin", "coordinator", "director_deportivo"].includes(user?.role);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filterTeam) params.set("team_id", filterTeam);
      if (filterStatus) params.set("status", filterStatus);
      const [i, s, t, p] = await Promise.all([
        api.get(`/injuries?${params}`),
        api.get(`/injuries/stats${filterTeam ? `?team_id=${filterTeam}` : ""}`),
        api.get("/teams"),
        api.get("/players"),
      ]);
      setItems(i.data);
      setStats(s.data);
      setTeams(t.data);
      setPlayers(p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filterTeam, filterStatus]);

  const nameOf = (pid) => players.find(p => p.id === pid)?.name || "—";
  const teamOf = (tid) => teams.find(t => t.id === tid)?.name || "—";

  const closeInjury = async (id) => {
    try { await api.post(`/injuries/${id}/close`); toast.success("Lesión marcada como recuperada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeInjury = async (id) => {
    if (!window.confirm("¿Eliminar esta lesión del registro?")) return;
    try { await api.delete(`/injuries/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><HeartPulse size={12} /> Enfermería</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Lesiones</h1>
          <p className="text-muted-foreground text-sm mt-2">Control de lesiones del club: tiempo de baja, motivo y estadísticas por tipo/gravedad.</p>
        </div>
        {canWrite && (
          <button data-testid="new-injury-btn" onClick={() => setCreating(true)} className="btn-volt">
            <Plus size={16} /> Nueva lesión
          </button>
        )}
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="injury-stats">
          <Card label="Total" value={stats.total} tid="stat-total" />
          <Card label="Activas" value={stats.active} tid="stat-active" accent />
          <Card label="Recuperadas" value={stats.recovered} tid="stat-recovered" />
          <Card label="Días medios de baja" value={stats.avg_recovery_days ?? "—"} suffix={stats.avg_recovery_days ? " d" : ""} tid="stat-avg" />
          <Card label="Tipo más frecuente" value={topTypeLabel(stats.by_type)} small tid="stat-top-type" />
        </div>
      )}

      {/* Filters */}
      <div className="border border-border p-4 mb-6 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-muted-foreground" />
        <select data-testid="inj-filter-team" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="h-9 bg-card border border-border px-3 font-mono text-xs focus:border-primary focus:outline-none">
          <option value="">Todos los equipos</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select data-testid="inj-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-9 bg-card border border-border px-3 font-mono text-xs focus:border-primary focus:outline-none">
          <option value="all">Todos los estados</option>
          <option value="active">Activas</option>
          <option value="recovered">Recuperadas</option>
        </select>
        <span className="text-[10px] font-mono text-muted-foreground ml-auto">{items.length} resultado(s)</span>
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="border-b border-border">
            <tr className="text-left overline">
              <th className="p-3">Jugador</th>
              <th className="p-3">Equipo</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Gravedad</th>
              <th className="p-3">Inicio</th>
              <th className="p-3">Alta</th>
              <th className="p-3">Días</th>
              <th className="p-3">Estado</th>
              <th className="p-3 text-right w-28"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`inj-row-${it.id}`} className="border-b border-border/60 hover:bg-white/[0.02]">
                <td className="p-3 font-heading font-bold uppercase tracking-tight">
                  <Link to={`/player/${it.player_id}`} className="hover:text-primary">{nameOf(it.player_id)}</Link>
                </td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{teamOf(it.team_id)}</td>
                <td className="p-3 font-mono text-xs">{TYPE_LABEL[it.type] || it.type}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 border font-mono text-[10px] ${SEV_COLOR[it.severity] || "border-border"}`}>{SEV_LABEL[it.severity] || it.severity}</span>
                </td>
                <td className="p-3 font-mono text-xs">{it.date_start}</td>
                <td className="p-3 font-mono text-xs">{it.date_end || <span className="text-muted-foreground/60">—</span>}</td>
                <td className="p-3 font-mono text-xs">{daysBetween(it.date_start, it.date_end)}</td>
                <td className="p-3">
                  {it.status === "active" ? (
                    <span className="px-2 py-0.5 border border-destructive text-destructive font-mono text-[10px] animate-pulse">ACTIVA</span>
                  ) : (
                    <span className="px-2 py-0.5 border border-emerald-500/60 text-emerald-400 font-mono text-[10px]">RECUP.</span>
                  )}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canWrite && it.status === "active" && (
                    <button data-testid={`inj-close-${it.id}`} onClick={() => closeInjury(it.id)} className="text-muted-foreground hover:text-emerald-400 mr-2" title="Marcar como recuperada">
                      <Check size={15} />
                    </button>
                  )}
                  {canWrite && (
                    <button data-testid={`inj-edit-${it.id}`} onClick={() => setEditing(it)} className="text-muted-foreground hover:text-primary mr-2" title="Editar">
                      <Save size={15} />
                    </button>
                  )}
                  {canDelete && (
                    <button data-testid={`inj-del-${it.id}`} onClick={() => removeInjury(it.id)} className="text-muted-foreground hover:text-destructive" title="Eliminar">
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-muted-foreground text-sm">Sin lesiones registradas.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Extra breakdown */}
      {stats && stats.ranking?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div className="border border-border">
            <div className="p-4 border-b border-border overline">Ranking de más lesionados</div>
            <div>
              {stats.ranking.map((r, ix) => (
                <div key={r.player_id} className="p-3 border-b border-border/40 last:border-b-0 flex items-center gap-3">
                  <div className="w-7 h-7 border border-primary/60 flex items-center justify-center font-mono text-xs">{ix + 1}</div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/player/${r.player_id}`} className="font-heading font-bold text-sm uppercase truncate hover:text-primary">{r.player_name}</Link>
                    <div className="text-[10px] font-mono text-muted-foreground">{r.active > 0 ? `${r.active} activa(s)` : "sin activas"}</div>
                  </div>
                  <div className="text-right">
                    <div className="metric-number text-lg">{r.count}</div>
                    <div className="text-[9px] font-mono text-muted-foreground">lesiones</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-border">
            <div className="p-4 border-b border-border overline">Distribución</div>
            <div className="p-4 space-y-4">
              <BreakdownBars title="Por tipo" data={stats.by_type} labels={TYPE_LABEL} />
              <BreakdownBars title="Por gravedad" data={stats.by_severity} labels={SEV_LABEL} />
            </div>
          </div>
        </div>
      )}

      {editing && <EditInjuryModal injury={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <CreateInjuryModal teams={teams} players={players} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CreateInjuryModal({ teams, players, onClose, onSaved }) {
  const [form, setForm] = useState({
    player_id: "",
    date_start: new Date().toISOString().slice(0, 10),
    minute: "",
    type: "muscular",
    severity: "leve",
    description: "",
  });
  // Filter players by team (team_id derived from selected player)
  const [filterTeam, setFilterTeam] = useState("");
  const visiblePlayers = filterTeam ? players.filter(p => p.team_id === filterTeam) : players;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.player_id) { toast.error("Selecciona un jugador"); return; }
    const player = players.find(p => p.id === form.player_id);
    if (!player) { toast.error("Jugador inválido"); return; }
    try {
      await api.post("/injuries", {
        player_id: form.player_id,
        team_id: player.team_id,
        date_start: form.date_start,
        minute: form.minute === "" ? null : Number(form.minute),
        type: form.type,
        severity: form.severity,
        description: form.description || null,
      });
      toast.success("Lesión creada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-card border border-border w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="create-injury-form">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">Nueva</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight">Registrar lesión</div>
          </div>
          <button type="button" onClick={onClose} data-testid="close-create-injury-btn" className="p-1 hover:text-primary"><X size={18} /></button>
        </div>
        <FormRow label="Filtrar por equipo (opcional)">
          <select data-testid="new-inj-team" value={filterTeam} onChange={(e) => { setFilterTeam(e.target.value); setForm({ ...form, player_id: "" }); }} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
            <option value="">Todos los equipos</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Jugador *">
          <select required data-testid="new-inj-player" value={form.player_id} onChange={(e) => setForm({ ...form, player_id: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
            <option value="">— Selecciona —</option>
            {visiblePlayers.map(p => <option key={p.id} value={p.id}>{p.name} {p.jersey_number ? `#${p.jersey_number}` : ""}</option>)}
          </select>
        </FormRow>
        <FormRow label="Fecha inicio *">
          <DateField value={form.date_start} onChange={(v) => setForm({ ...form, date_start: v })} testid="new-inj-date" />
        </FormRow>
        <div className="grid grid-cols-3 gap-3">
          <FormRow label="Minuto">
            <input type="number" data-testid="new-inj-min" value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} placeholder="—" className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
          </FormRow>
          <FormRow label="Tipo *">
            <select data-testid="new-inj-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormRow>
          <FormRow label="Gravedad *">
            <select data-testid="new-inj-sev" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
              {Object.entries(SEV_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormRow>
        </div>
        <FormRow label="Descripción breve">
          <input type="text" data-testid="new-inj-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ej: desgarro isquios pierna derecha" className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </FormRow>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} data-testid="cancel-create-injury-btn" className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
          <button type="submit" data-testid="submit-new-inj-btn" className="btn-volt">Crear lesión</button>
        </div>
      </form>
    </div>
  );
}

function EditInjuryModal({ injury, onClose, onSaved }) {
  const [form, setForm] = useState({
    date_end: injury.date_end || "",
    type: injury.type || "otro",
    severity: injury.severity || "leve",
    description: injury.description || "",
    notes: injury.notes || "",
    status: injury.status || "active",
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.date_end) delete payload.date_end;
      await api.put(`/injuries/${injury.id}`, payload);
      toast.success("Lesión actualizada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-card border border-border w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="edit-injury-form">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">Editar lesión</div>
            <div className="text-xs font-mono text-muted-foreground mt-1">Registrada {injury.created_at?.slice(0, 10)} · min {injury.minute ?? "—"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:text-primary" data-testid="close-edit-injury-btn"><X size={18} /></button>
        </div>
        <FormRow label="Fecha alta médica (opcional)">
          <DateField
            value={form.date_end}
            onChange={(v) => setForm({ ...form, date_end: v, status: v ? "recovered" : "active" })}
            testid="inj-date-end"
            placeholder="Sin fecha alta"
          />
        </FormRow>
        <div className="grid grid-cols-2 gap-3">
          <FormRow label="Tipo">
            <select data-testid="inj-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormRow>
          <FormRow label="Gravedad">
            <select data-testid="inj-sev" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
              {Object.entries(SEV_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormRow>
        </div>
        <FormRow label="Descripción">
          <input type="text" data-testid="inj-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </FormRow>
        <FormRow label="Notas médicas / evolución">
          <textarea data-testid="inj-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </FormRow>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} data-testid="cancel-edit-injury-btn" className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
          <button type="submit" data-testid="save-inj-btn" className="btn-volt">Guardar</button>
        </div>
      </form>
    </div>
  );
}

function Card({ label, value, suffix = "", tid, accent, small }) {
  return (
    <div className={`border p-4 ${accent ? "border-destructive" : "border-border"}`} data-testid={tid}>
      <div className="overline">{label}</div>
      <div className={`${small ? "text-xl font-heading font-bold uppercase" : "metric-number text-3xl"} mt-2 ${accent ? "text-destructive" : ""}`}>
        {value}<span className="text-sm text-muted-foreground">{suffix}</span>
      </div>
    </div>
  );
}
function BreakdownBars({ title, data, labels }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0) || 1;
  return (
    <div>
      <div className="overline mb-2">{title}</div>
      <div className="space-y-1.5">
        {Object.entries(data).map(([k, v]) => {
          const pct = Math.round((v / total) * 100);
          return (
            <div key={k}>
              <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span>{labels[k] || k}</span>
                <span className="font-mono text-primary">{v} <span className="text-muted-foreground">({pct}%)</span></span>
              </div>
              <div className="h-1 bg-border"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
function FormRow({ label, children }) {
  return (<div><label className="overline block mb-1">{label}</label>{children}</div>);
}
function topTypeLabel(byType) {
  if (!byType) return "—";
  const entries = Object.entries(byType);
  if (!entries.length) return "—";
  const [k, v] = entries.sort((a, b) => b[1] - a[1])[0];
  return v > 0 ? (TYPE_LABEL[k] || k) : "—";
}
